/**
 * Integration utilities for zip code validation and app_state cookie management
 * This module provides integration between zip code validation results and app_state cookies
 * for load balancer redirection functionality similar to patient-onboarding
 */

import { setAppStateCookie, clearAppStateCookie, getAppStateCookie } from './cookieUtils';

/**
 * Interface for zip code validation response data
 */
export interface ZipCodeValidationResponse {
  city?: string;
  state?: string;
  stateCode?: string;
  enabled_obie?: boolean;
  lifestance_state?: boolean;
  insurances?: Array<string>;
  type_of_cares?: Array<string>;
}

/**
 * Handle state cookie when zip code is successfully validated
 * Sets the app_state cookie if state information is available and updates URL with both state and zipCode
 * @param responseData - The response data from zip code validation API
 * @param zipCode - The zip code that was validated
 */
export const handleZipCodeValidationSuccess = (
  responseData: ZipCodeValidationResponse,
  zipCode?: string,
): void => {
  const stateCode = responseData.stateCode || responseData.state;

  if (stateCode) {
    setAppStateCookie(stateCode);

    // Also update URL with both state and zipCode
    if (zipCode) {
      updateUrlWithStateAndZip(stateCode, zipCode);
    }
  }
};

/**
 * Clear app_state cookie when zip code validation fails or changes
 */
export const handleZipCodeValidationClear = (): void => {
  clearAppStateCookie();
};

/**
 * Update URL parameters with state and zip code information
 * This will cause a full page reload to the new URL
 * @param stateCode - The state code to add to URL
 * @param zipCode - The zip code to add to URL
 */
export const updateUrlWithStateAndZip = (stateCode: string, zipCode?: string): void => {
  const currentParams = new URLSearchParams(window.location.search);

  currentParams.set('state', stateCode);
  currentParams.set(
    'r',
    Array.from({ length: 3 }, () => String.fromCharCode(97 + Math.floor(Math.random() * 26))).join(
      '',
    ),
  );

  if (zipCode) {
    currentParams.set('zipCode', zipCode);
  }

  const newUrl = `${window.location.pathname}?${currentParams.toString()}`;

  // Use window.location.href for full page reload
  window.location.href = newUrl;
};

/**
 * Initialize app state from URL parameters on page load
 * This function should be called when the application initializes to restore
 * app_state cookie from URL parameters if available
 */
export const initializeAppStateFromUrl = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const stateFromUrl = urlParams.get('state');

  if (stateFromUrl) {
    const currentCookieState = getAppStateCookie();

    // Only set cookie if it doesn't already match
    if (currentCookieState !== stateFromUrl) {
      setAppStateCookie(stateFromUrl);
    }
  }
};

/**
 * Read the current state value from URL parameters
 * @returns The state code from URL parameters or null if not found
 */
export const getStateFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('state');
};

/**
 * Read the current zip code value from URL parameters
 * @returns The zip code from URL parameters or null if not found
 */
export const getZipCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('zipCode');
};

/**
 * Get both state and zip code from URL parameters
 * @returns Object containing state and zipCode from URL, or null values if not found
 */
export const getLocationFromUrl = (): { state: string | null; zipCode: string | null } => ({
  state: getStateFromUrl(),
  zipCode: getZipCodeFromUrl(),
});

/**
 * Check if current state requires cookie update based on validation response
 * @param responseData - The response data from zip code validation
 * @param currentZipCode - The current zip code being validated
 * @returns boolean indicating if cookie should be updated
 */
export const shouldUpdateAppStateCookie = (
  responseData: ZipCodeValidationResponse,
  currentZipCode: string,
): boolean => {
  const stateCode = responseData.stateCode || responseData.state;
  const currentCookieState = getAppStateCookie();

  return !!(stateCode && stateCode !== currentCookieState);
};
